// src/pages/AddFood.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ApiUrl } from "Services/Services";
import { toast } from "react-toastify";

export default function AddFood() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    categoryId: "",
    image: null,
    ingredients: [],
  });

  const [categories, setCategories] = useState([]);
  const [allIngredients, setAllIngredients] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchIngredients();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/category`);
      setCategories(res.data.data || []);
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  const fetchIngredients = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/ingredients`);
      setAllIngredients(res.data.data || []);
    } catch (err) {
      console.error("Error fetching ingredients", err);
    }
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setForm({ ...form, image: file });
    setImagePreview(URL.createObjectURL(file));
  };

  const handleIngredientToggle = (id) => {
    const updatedIngredients = [...form.ingredients];
    const index = updatedIngredients.findIndex((ing) => ing.ingredientId === id);

    if (index === -1) {
      updatedIngredients.push({ ingredientId: id, quantity: 1 });
    } else {
      updatedIngredients.splice(index, 1);
    }

    setForm({ ...form, ingredients: updatedIngredients });
  };

  const handleIngredientQtyChange = (id, qty) => {
    const updatedIngredients = [...form.ingredients];
    const index = updatedIngredients.findIndex((ing) => ing.ingredientId === id);
    if (index > -1) {
      updatedIngredients[index].quantity = Number(qty);
    }
    setForm({ ...form, ingredients: updatedIngredients });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("description", form.description);
      data.append("price", form.price);
      data.append("categoryId", form.categoryId);
      if (form.image) data.append("image", form.image);
      data.append("ingredients", JSON.stringify(form.ingredients));

      await axios.post(`${ApiUrl}/food`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
       toast.success('Food Added');
      navigate("/admin/food");
    } catch (err) {
      console.error("Error creating food", err);
    }
  };

  return (
    <div className="max-w-4xl mt-10 mx-auto bg-white shadow-lg rounded-xl p-6">
      <h2 className="text-3xl font-bold mb-6 border-b pb-3">Add New Food</h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div>
          <label className="font-medium">Food Name</label>
          <input
            type="text"
            name="name"
            placeholder="Enter food name"
            value={form.name}
            onChange={handleInputChange}
            className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-green-400"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="font-medium">Description</label>
          <textarea
            name="description"
            placeholder="Write food description..."
            value={form.description}
            onChange={handleInputChange}
            className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-green-400"
            rows="3"
          ></textarea>
        </div>

        {/* Price & Category */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="font-medium">Price (PKR)</label>
            <input
              type="number"
              name="price"
              placeholder="e.g. 599"
              value={form.price}
              onChange={handleInputChange}
              className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-green-400"
              required
            />
          </div>

          <div>
            <label className="font-medium">Category</label>
            <select
              name="categoryId"
              value={form.categoryId}
              onChange={handleInputChange}
              className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-green-400"
              required
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Image Upload */}
        <div>
          <label className="font-medium pt-5">Food Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-green-400"
          />
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              className="mt-3 w-32 h-32 object-cover rounded-lg border"
            />
          )}
        </div>

        {/* Ingredients */}
        <div>
          <label className="font-medium mb-2 block">Select Ingredients</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {allIngredients.map((ing) => {
              const selected = form.ingredients.find(
                (i) => i.ingredientId === ing.id
              );
              return (
                <div
                  key={ing.id}
                  className={`p-3 rounded-lg border cursor-pointer flex flex-col items-start ${
                    selected
                      ? "bg-green-100 border-green-400"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                  onClick={() => handleIngredientToggle(ing.id)}
                >
                  <div className="flex justify-between w-full">
                    <span className="font-medium">{ing.name}</span>
                    {selected && <span className="text-green-600">✔</span>}
                  </div>
                  {selected && (
                    <input
                      type="number"
                      min="1"
                      value={selected.quantity}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleIngredientQtyChange(ing.id, e.target.value)
                      }
                      className="mt-2 w-20 p-1 border rounded text-sm"
                      placeholder="Qty"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow"
          >
            Save Food
          </button>
        </div>
      </form>
    </div>
  );
}
